<template>
  <section class="bz-list-group" :aria-labelledby="title ? titleId : undefined">
    <component :is="`h${level}`" v-if="title" :id="titleId" class="bz-list-group__title">{{ title }}</component>
    <div class="bz-list-group__items">
      <slot />
    </div>
  </section>
</template>

<script>
let next = 0;

/**
 * A group of rows: a title, and one card that holds them, a hairline between two rows — the grouped
 * list both systems share (Android's settings, iOS's, Shopify's and Square's).
 *
 * @rule   Its children are `BzListRow`s, or anything drawn as one.
 * @rule   The rows draw the hairlines (from their text, not from the card's edge, so an icon column
 *         reads as one) and their own press; the card draws the surface, its edge and its corners. The
 *         first and last rows take the card's inner corners, so a pressed one never pokes out of it.
 * @why    Not PrimeVue's Panel or Fieldset (docs/migracion-ui/mapa-componentes.md): Panel folds, which
 *         a list of settings must not, and Fieldset is the semantics of a form. A titled list is
 *         layout, and layout is tokens.
 */
export default {
  name: 'BzListGroup',
  props: {
    title: { type: String, default: undefined },
    // The title's heading level: 2 on a screen whose <h1> is its header; one more inside a section.
    level: { type: Number, default: 2, validator: (n) => n >= 2 && n <= 6 },
  },
  data() {
    return { titleId: `bz-list-group-${++next}` };
  },
};
</script>

<style scoped>
/* 32 between groups and 8 from a group's title to its card: the title is read with what it names, and
   a group stands apart from the next one by a whole step of the scale more than its rows do. */
.bz-list-group {
  margin: 0 0 var(--bz-space-7);
}

/* 16 at 500 in the second ink: a step over a row's hint (14) and one under its title (18), so the
   three never read as the same size. */
.bz-list-group__title {
  margin: 0 var(--bz-space-4) var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
  color: var(--bz-ink-2);
}

.bz-list-group__items {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

/* The card's inner corners, on the rows at its ends (see the rule above). */
.bz-list-group__items > :deep(:first-child) {
  border-top-left-radius: calc(var(--bz-radius-lg) - 1px);
  border-top-right-radius: calc(var(--bz-radius-lg) - 1px);
}

.bz-list-group__items > :deep(:last-child) {
  border-bottom-left-radius: calc(var(--bz-radius-lg) - 1px);
  border-bottom-right-radius: calc(var(--bz-radius-lg) - 1px);
}
</style>
